import { useMemo, useState } from "react";
import { bool } from "prop-types";
import { observer } from "mobx-react";
import { differenceInCalendarMonths, startOfMonth } from "date-fns";
import { MAX_TIMELINE_LENGTH, Tables, hashCode } from "@repo/utils";
import { generateTimelineCalendar } from "@repo/structure";
import {
  useEstimateStructure,
  usePresetFieldNames,
  useEstimateEditorStore,
  useVisibilityModeCellClassName,
  useEstimateEditorSettings,
  useTableExpandHandler,
  ProjectTimelineSettings,
  TableTitle,
  VisibilityButton,
  EstimateTableSettingsProvider,
} from "@tools";
import { ButtonSwitch } from "@components";
import { Grid, Collapse } from "@material-ui/core";
import { TimelineContainer } from "./components/TimelineContainer";
import useStyle from "@tools/EstimateEditor/styles/CommonTableStyles.style";
import classnames from "classnames";

export const TableTimeline = observer(({
  tableBreakdownNext,
  tableBreakdownPrev,
  isLastTable,
}) => {
  const structure = useEstimateStructure();
  const { visibilityMode } = useEstimateEditorStore();
  const {
    allowEdition,
    isProposal,
    blockExpansions,
    forceExpansionArrows,
    showPdfCover,
    isPdfPrinterView,
  } = useEstimateEditorSettings();
  const { getFieldName } = usePresetFieldNames(isProposal);
  const classes = useStyle();

  const [ minimumVisibleMonths, setMinimumVisibleMonths ] = useState(0);
  const [ fullSectionActions, setFullSectionActions ] = useState(false);
  const [ billingChartWorkTypes, setBillingChartWorkTypes ] = useState([]);

  const {
    displayTotalTwoValues,
    displayAnyValue,
    usedTimelineMode,
    workTypesHaveResources,
  } = structure;

  const {
    showTimeline,
    useMinMax,
    maxValuesAsBuffer,
  } = structure.settings;

  const { hideTimeline } = structure.visibility;

  const className = useVisibilityModeCellClassName(
    hideTimeline || !displayAnyValue,
  );

  const useExpandCell = useMemo(
    () => !isProposal || workTypesHaveResources || forceExpansionArrows,
    [isProposal, workTypesHaveResources],
  );

  const timelineModeValues = useMemo(
    () => [
      {
        value: "min",
        label: getFieldName("pt-min"),
        className: classnames(
          "pt-min",
          `preset-timelineMode${usedTimelineMode === "min" ? "Active" : ""}`,
        ),
      },
      {
        value: "max",
        label: getFieldName("pt-max"),
        className: classnames(
          "pt-max",
          `preset-timelineMode${usedTimelineMode === "max" ? "Active" : ""}`,
        ),
      },
    ],
    [usedTimelineMode, getFieldName("pt-max"), getFieldName("pt-min")],
  );


  const activeStartDate = structure.settings.timelineStartDate;
  const activeEndDate = structure.currentTimeline?.lastSectionEndDate;
  const structureMonthCount =
    activeStartDate && activeEndDate
      ? differenceInCalendarMonths(
      startOfMonth(activeEndDate),
      startOfMonth(activeStartDate),
    ) + 1
      : 1;

  const effectiveMonthCount = useMemo(() => (
    Math.max(
      minimumVisibleMonths || 0,
      Math.min(structureMonthCount, MAX_TIMELINE_LENGTH)
    )
  ), [ minimumVisibleMonths, structureMonthCount ]);

  const months = useMemo(() => (
    activeStartDate
      ? generateTimelineCalendar(activeStartDate, effectiveMonthCount)
      : []
  ), [ activeStartDate, effectiveMonthCount ]);

  const handleTimelineMode = (mode) => {
    if (blockExpansions) return;
    structure.historyManager.startGroup();
    structure.settings.setTimelineMode(mode);
    structure.generateTimeline();
    structure.historyManager.stopGroup();
  };

  const handleOpenedCharts = (open, workTypes) => {
    setBillingChartWorkTypes((state) => {
      if(open) state.push(...workTypes);
      else workTypes.forEach(wT => {
        if(state.indexOf(wT) >= 0)
          state.splice(state.indexOf(wT), 1);
      })
      return [...state];
    })
  }

  const { handleOpen, ...collapseProps } = useTableExpandHandler({
    expandable: !blockExpansions,
    isOpened: showTimeline,
    onExpand: structure.settings.setShowTimeline,
  });

  return (
    <EstimateTableSettingsProvider
      useExpandCell={useExpandCell}
      timelineMonths={months}
      fullSectionActions={fullSectionActions && allowEdition}
      setFullSectionActions={setFullSectionActions}
      billingChartWorkTypes={billingChartWorkTypes}
      setOpenedCharts={handleOpenedCharts}
    >
      <Grid
        item
        container
        id={Tables.TIMELINE}
        justifyContent="center"
        alignItems="center"
        className={classnames(
          "preset-paperTimeline",
          classes.whiteContainer,
          tableBreakdownNext && classes.whiteContainerLast,
          tableBreakdownPrev && classes.whiteContainerFirst,
          isLastTable && !isPdfPrinterView && classes.paddingBottom,
        )}
      >
        <Grid
          item
          container
          justifyContent="flex-end"
          className={classnames(
            "vCon",
            showPdfCover && "pdf-mode",
            isPdfPrinterView && "printer-mode",
            classes.root,
          )}
        >
          <TableTitle
            disabled={visibilityMode && (hideTimeline || !displayAnyValue)}
            expandable={!blockExpansions}
            presetClass="timeline"
            buttonPresetClass="preset-tableTimelineExpandButton"
            id={hashCode(Tables.TIMELINE)}
            title={getFieldName("pt-timeline")}
            showContent={showTimeline}
            onContentAction={handleOpen}
            className={classnames(isPdfPrinterView && "printer-mode")}
            action={
              <>
                {allowEdition && <ProjectTimelineSettings />}
                {visibilityMode && (
                  <VisibilityButton
                    hidden={hideTimeline}
                    onChange={structure.visibility.setHideTimeline}
                  />
                )}
              </>
            }
          >
            {(allowEdition || useMinMax) && (
              <Grid item container alignItems="center" className="w-max my-1">
                {displayTotalTwoValues && !maxValuesAsBuffer && (
                  <ButtonSwitch
                    value={usedTimelineMode}
                    setValue={handleTimelineMode}
                    values={timelineModeValues}
                    containerClassName={classes.buttonSwitch}
                    size="small"
                  />
                )}
              </Grid>
            )}
          </TableTitle>
          <Collapse
            in={showTimeline}
            timeout="auto"
            mountOnEnter
            unmountOnExit
            className={classnames(
              showPdfCover && "pdf-mode",
              isPdfPrinterView && "printer-mode",
              classes.collapse,
              className,
            )}
            {...collapseProps}
          >
            <TimelineContainer
              fullSectionActions={fullSectionActions}
              minimumVisibleMonths={minimumVisibleMonths}
              setMinimumVisibleMonths={setMinimumVisibleMonths}
            />
          </Collapse>
        </Grid>
      </Grid>
    </EstimateTableSettingsProvider>
  );
});

TableTimeline.propTypes = {
  tableBreakdownNext: bool,
  tableBreakdownPrev: bool,
  isLastTable: bool,
};
